import { useEffect, useState, FC, FocusEvent, ChangeEvent } from 'react';
import { InputField, InputFieldProps } from 'app/components/Core';
import NumberFormat from 'react-number-format';

import { getDurationFormat } from '../utils';

interface DurationTextFieldProps {
  value: number | '';
  onChange: (seconds: number | '') => void;
  error?: string;
  label?: string;
  disabled?: boolean;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  sx?: InputFieldProps['sx'];
}

const isValid = (minutes: number, seconds: number) =>
  minutes < 60 && seconds < 60;

export const DurationTextField: FC<DurationTextFieldProps> = ({
  value: valueProp,
  error: errorProp,
  onChange,
  onBlur,
  label = 'Duration',
  disabled,
  sx,
}) => {
  const [error, setError] = useState('');
  const [value, setValue] = useState(
    valueProp ? getDurationFormat(valueProp) : undefined
  );

  const handleOnChange = ({
    target: { value: formattedValue },
  }: ChangeEvent<HTMLInputElement>) => {
    const value = (formattedValue || '').replace('_', '').replace(':', '');
    setValue(formattedValue);
    if (value === '') {
      setError(value);
      return onChange(value);
    }

    if (!formattedValue.includes('_')) {
      const [hours, minutes, seconds] = formattedValue.split(':').map(Number);
      if (isValid(minutes, seconds)) {
        setError('');
        onChange(hours * 3600 + minutes * 60 + seconds);
      } else {
        setError('Format is not valid');
      }
    }
    return undefined;
  };

  useEffect(() => {
    setValue(valueProp || valueProp === 0 ? getDurationFormat(valueProp) : '');
  }, [valueProp]);

  return (
    <NumberFormat<InputFieldProps>
      customInput={InputField}
      placeholder="hh:mm:ss"
      data-testid={label}
      name="duration"
      format="##:##:##"
      mask="_"
      label={label}
      value={value}
      onChange={handleOnChange}
      isInvalid={Boolean(error || errorProp)}
      errorMessage={error || errorProp}
      helperText={errorProp || error}
      isDisabled={disabled}
      onBlur={onBlur}
      errorMessageProps={{ 'data-testid': 'duration-field-error-text' }}
      sx={sx}
    />
  );
};
